/**
 * Die Suchleiste ueber der Liste.
 *
 * Ein Feld traegt die Hauptlast: Wer einen Tauchgang sucht, erinnert sich an
 * einen Namen, einen Buddy oder ein Tier in den Notizen — nicht an eine
 * Tiefenklasse. Die uebrigen Filter sind eingeklappt, bis jemand sie braucht.
 */

import { beschreibung, buddys, jahre, plaetze, reisen } from '../suche.js';

const GASE = [['', 'alle Gase'], ['air', 'Luft'], ['eanx', 'Nitrox'], ['trimix', 'Trimix']];

export function suchleiste(alle, kriterien, aendern) {
  const el = document.createElement('form');
  el.className = 'suche';
  el.setAttribute('role', 'search');
  el.innerHTML = `
    <div class="suche-zeile">
      <input class="suche-text" type="search" name="q" placeholder="Platz, Buddy, Notiz …"
             autocomplete="off" spellcheck="false" aria-label="Volltextsuche">
      <button type="button" class="suche-mehr" aria-expanded="false">Filter</button>
    </div>
    <div class="suche-filter" hidden>
      <label>Jahr
        <select name="jahr"><option value="">alle</option></select>
      </label>
      <label>Tauchplatz
        <input name="platz" list="plaetze" autocomplete="off" placeholder="alle">
      </label>
      <label>Reise
        <input name="reise" list="reisen" autocomplete="off" placeholder="alle">
      </label>
      <label>Buddy
        <input name="buddy" list="buddys" autocomplete="off" placeholder="alle">
      </label>
      <label>Gas
        <select name="gas"></select>
      </label>
      <label class="tiefe">Tiefe
        <span>
          <input name="von" type="number" min="0" max="150" step="1" placeholder="von">
          <input name="bis" type="number" min="0" max="150" step="1" placeholder="bis">
          <span class="einheit">m</span>
        </span>
      </label>
      <label class="schalter"><input type="checkbox" name="profil"> nur mit Profil</label>
      <label class="schalter"><input type="checkbox" name="pruefen"> zu prüfen</label>
    </div>
    <datalist id="plaetze"></datalist>
    <datalist id="reisen"></datalist>
    <datalist id="buddys"></datalist>
    <p class="suche-stand" role="status" aria-live="polite"></p>`;

  const jahrfeld = el.querySelector('select[name=jahr]');
  for (const j of jahre(alle)) jahrfeld.append(new Option(j, j));
  const gasfeld = el.querySelector('select[name=gas]');
  for (const [wert, text] of GASE) gasfeld.append(new Option(text, wert));
  const vorschlaege = el.querySelector('#plaetze');
  for (const [name] of plaetze(alle).slice(0, 300)) vorschlaege.append(new Option(name));
  const reisewahl = el.querySelector('#reisen');
  for (const name of reisen(alle)) reisewahl.append(new Option(name));
  const buddywahl = el.querySelector('#buddys');
  for (const name of buddys(alle).slice(0, 300)) buddywahl.append(new Option(name));

  const felder = {
    q: el.querySelector('input[name=q]'),
    jahr: jahrfeld,
    platz: el.querySelector('input[name=platz]'),
    reise: el.querySelector('input[name=reise]'),
    buddy: el.querySelector('input[name=buddy]'),
    gas: gasfeld,
    von: el.querySelector('input[name=von]'),
    bis: el.querySelector('input[name=bis]'),
    profil: el.querySelector('input[name=profil]'),
    pruefen: el.querySelector('input[name=pruefen]'),
  };
  const filterbereich = el.querySelector('.suche-filter');
  const mehr = el.querySelector('.suche-mehr');

  function uebernimm(k) {
    felder.q.value = k.text ?? '';
    felder.jahr.value = k.jahr ?? '';
    felder.platz.value = k.platz ?? '';
    felder.reise.value = k.reise ?? '';
    felder.buddy.value = k.buddy ?? '';
    felder.gas.value = k.gas ?? '';
    felder.von.value = k.tiefeVon ?? '';
    felder.bis.value = k.tiefeBis ?? '';
    felder.profil.checked = Boolean(k.nurProfil);
    felder.pruefen.checked = Boolean(k.nurPruefen);
    // Sind Filter aktiv, zeigt die Leiste sie — sonst sucht jemand im Blinden.
    const aktiv = Boolean(k.jahr || k.platz || k.reise || k.buddy || k.gas
      || k.tiefeVon || k.tiefeBis || k.nurProfil || k.nurPruefen);
    if (aktiv) {
      filterbereich.hidden = false;
      mehr.setAttribute('aria-expanded', 'true');
    }
    mehr.classList.toggle('aktiv', aktiv);
  }

  function lies() {
    const zahl = (feld) => (feld.value === '' ? null : Number(feld.value));
    return {
      text: felder.q.value.trim(),
      jahr: felder.jahr.value || null,
      platz: felder.platz.value.trim(),
      reise: felder.reise.value.trim(),
      buddy: felder.buddy.value.trim(),
      gas: felder.gas.value || null,
      tiefeVon: zahl(felder.von),
      tiefeBis: zahl(felder.bis),
      nurProfil: felder.profil.checked,
      nurPruefen: felder.pruefen.checked,
    };
  }

  el.addEventListener('submit', (e) => e.preventDefault());
  el.addEventListener('input', () => aendern(lies()));
  el.addEventListener('change', () => aendern(lies()));
  mehr.addEventListener('click', () => {
    filterbereich.hidden = !filterbereich.hidden;
    mehr.setAttribute('aria-expanded', String(!filterbereich.hidden));
    if (!filterbereich.hidden) felder.jahr.focus();
  });

  uebernimm(kriterien);

  el.zeigeStand = (treffer, gesamt, k) => {
    const stand = el.querySelector('.suche-stand');
    if (treffer === gesamt) {
      stand.textContent = `${gesamt} Tauchgänge`;
      stand.classList.remove('gefiltert');
      return;
    }
    const text = beschreibung(k);
    stand.textContent = treffer === 0
      ? `Nichts gefunden für ${text}`
      : `${treffer} von ${gesamt} Tauchgängen · ${text}`;
    stand.classList.add('gefiltert');
    const zuruecksetzen = document.createElement('button');
    zuruecksetzen.type = 'button';
    zuruecksetzen.className = 'zuruecksetzen';
    zuruecksetzen.textContent = 'zurücksetzen';
    zuruecksetzen.addEventListener('click', () => {
      el.reset();
      felder.jahr.value = '';
      felder.gas.value = '';
      aendern(lies());
    });
    stand.append(' ', zuruecksetzen);
  };

  el.fokussiere = () => felder.q.focus();
  return el;
}
